<vs-dialog #dialog [config]="{ dialogContainerClass: 'max-w-[600px]' }">
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      icon: 'database-01',
      iconTheme: 'primary',
      title: element?.id
        ? 'Edit Element'
        : parentTitle
          ? 'Add Child Element'
          : 'Create Element',
      subtitle: element?.id
        ? 'Update the details of this element.'
        : parentTitle
          ? 'Add a child element under ' + parentTitle + '.'
          : 'Add a new element to the ' + (dimension?.title || 'selected dimension') + ' dimension.'
    }"
  ></vs-dialog-header>

  <form [formGroup]="form" (ngSubmit)="save()">
    <!-- Parent Context (only when creating child) -->
    <ng-container *ngIf="parentTitle && !element?.id">
      <div class="flex items-center gap-2 px-3 py-2 mb-4 bg-gray-50 border border-gray-200 rounded-lg">
        <vs-icon name="git-branch-01" size="16" class="text-gray-500"></vs-icon>
        <span class="text-sm text-gray-600">Parent:</span>
        <span class="text-sm font-medium text-gray-900 truncate">{{ parentTitle }}</span>
      </div>
    </ng-container>

    <!-- Title -->
    <vs-form-field
      appearance="responsive"
      [theme]="form.get('title')?.touched && form.get('title')?.invalid ? 'danger' : 'primary'"
      [config]="{
        label: 'Title',
        description: 'The name of this element (e.g., Reserve, Premium, Class 1)'
      }"
    >
      <input vsInput type="text" formControlName="title" placeholder="Enter element title" />

      <span
        slot="hint"
        class="text-danger"
        *ngIf="form.get('title')?.touched && form.get('title')?.hasError('required')"
      >
        Title is required
      </span>

      <span slot="hint" class="text-danger" *ngIf="form.get('title')?.hasError('maxlength')">
        Title must be less than 255 characters
      </span>
    </vs-form-field>

    <vs-divider class="my-4"></vs-divider>

    <!-- Description -->
    <vs-form-field
      appearance="responsive"
      [theme]="form.get('description')?.touched && form.get('description')?.invalid ? 'danger' : 'primary'"
      [config]="{
        label: 'Description',
        description: 'Optional description for this element'
      }"
    >
      <textarea vsInput rows="3" formControlName="description" placeholder="Enter description (optional)"></textarea>

      <span slot="hint" class="text-danger" *ngIf="form.get('description')?.hasError('maxlength')">
        Description must be less than 255 characters
      </span>
    </vs-form-field>

    <!-- Fee Type Dimension Fields -->
    <ng-container *ngIf="isFeeTypeDimension">
      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="horizontal"
        class="[&>div>div:last-child]:min-w-[unset]"
        [config]="{
          label: 'Unsummed offset',
          description: 'Show this fee\'s base and seller-specific cells as separate lines in a seller\'s commission report, instead of combining them into one total.'
        }"
      >
        <input vsInput type="checkbox" appearance="toggle" class="block ml-auto" formControlName="unsummedOffset" />
      </vs-form-field>

      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="horizontal"
        class="[&>div>div:last-child]:min-w-[unset]"
        [config]="{
          label: 'Include Upstream Distributor Fees',
          description: 'Include the fees of the distributors above a seller in the hierarchy when pricing this fee.'
        }"
      >
        <input
          vsInput
          type="checkbox"
          appearance="toggle"
          class="block ml-auto"
          formControlName="includeUpstreamDistributorFees"
        />
      </vs-form-field>

      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="horizontal"
        class="[&>div>div:last-child]:min-w-[unset]"
        [config]="{
          label: 'Is Net',
          description: 'Net fees are due for every warranty sold. Non-net fees will be collected from the buyer but need not be remitted from the seller to the administrator.'
        }"
      >
        <input vsInput type="checkbox" appearance="toggle" class="block ml-auto" formControlName="isNet" />
      </vs-form-field>

      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="horizontal"
        class="[&>div>div:last-child]:min-w-[unset]"
        [config]="{
          label: 'One-time fee',
          description: 'Charged once, in full with the first payment of the contract (e.g. an initiation fee), and never on renewal. Recurring fees are charged with every payment and renewal.'
        }"
      >
        <input vsInput type="checkbox" appearance="toggle" class="block ml-auto" formControlName="isOneTime" />
      </vs-form-field>

      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="responsive"
        [config]="{
          label: 'Default recipient',
          description: 'Who this fee is intended for. A rating-matrix cell can override it. Does not affect pricing. Manage parties on the Parties tab.'
        }"
      >
        <ws-fee-recipient-picker [value]="recipient" (valueChanges)="recipient = $event"></ws-fee-recipient-picker>
      </vs-form-field>
    </ng-container>

    <!-- Scalar Dimension Fields -->
    <ng-container *ngIf="dimension?.isScalar">
      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="responsive"
        [theme]="
          (form.get('lowerBound')?.touched || form.touched) &&
          form.hasError('boundsInvalid')
            ? 'danger'
            : 'primary'
        "
        [config]="{
          label: 'Lower Bound',
          description: 'Inclusive lower bound'
        }"
      >
        <input vsInput type="number" formControlName="lowerBound" placeholder="Enter lower bound" />
      </vs-form-field>

      <vs-divider class="my-4"></vs-divider>

      <vs-form-field
        appearance="responsive"
        [theme]="
          (form.get('upperBound')?.touched || form.touched) &&
          form.hasError('boundsInvalid')
            ? 'danger'
            : 'primary'
        "
        [config]="{
          label: 'Upper Bound',
          description: 'Exclusive upper bound'
        }"
      >
        <input vsInput type="number" formControlName="upperBound" placeholder="Enter upper bound" />

        <span
          slot="hint"
          class="text-danger"
          *ngIf="
            (form.get('upperBound')?.touched || form.touched) &&
            form.hasError('boundsInvalid')
          "
        >
          Upper bound must be greater than lower bound
        </span>
      </vs-form-field>
    </ng-container>

    <!-- Actions -->
    <div class="flex gap-2 mt-6">
      <button vs-button appearance="outline" class="flex-1" type="button" (click)="cancel()" [disabled]="saving">
        Cancel
      </button>

      <button vs-button theme="primary" class="flex-1" type="submit" [disabled]="saving || form.invalid">
        <vs-icon slot="start" [class.animate-spin]="saving" [name]="saving ? 'refresh-cw-04' : 'save-01'"></vs-icon>

        {{ saving ? 'Saving...' : element?.id ? 'Update' : 'Create' }}
      </button>
    </div>
  </form>
</vs-dialog>
